import React, { useEffect, useMemo, useState } from 'react';
import { css, cx } from '@emotion/css';
import DeckGLView from './DeckGLView';
import FloatingUI from './FloatingUI';
import { useHexMapModel } from './contexts/hexMapModel';
import { useHex2DLayerData } from './contexts/hex2DLayer';
import { useDeckGLViewState } from './contexts/deckGLViewState';
import { useHighlightLayer } from './contexts/highlightLayer';
import { useTools } from './contexts/tools';
import { maybe } from '../../../utils';
import { PickingInfoPoint } from '../math/PickingInfoPoint';
const colorOptions = [
    { name: 'White', color: [255, 255, 255] },
    { name: 'Green', color: [0, 128, 0] },
    { name: 'Blue', color: [0, 0, 255] },
    { name: 'Gray', color: [128, 128, 128] },
];
const mapEditorWrapperStyle = css `
  display: flex;
  align-items: stretch;
`;
const editorPaneWrapperStyle = css `
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  position: relative;
`;
const HexMapEditor = ({ className, resource, onChange }) => {
    const hexMapModel = useMemo(() => ({ ...resource.data }), [resource]);
    const [colorOption, setColorOption] = useState(colorOptions[0]);
    const paletteProps = useMemo(() => ({ initialSelection: colorOption, colors: colorOptions, onChange: setColorOption }), [colorOption, setColorOption]);
    const viewStateContext = useDeckGLViewState();
    const { editorState, viewState, updateEditorState } = viewStateContext;
    const hexMapModelContext = useHexMapModel({ hexmap: resource, onChange });
    const hex2DLayerContext = useHex2DLayerData({ hexes: hexMapModel, zoom: viewState.zoom });
    const highlightLayerContext = useHighlightLayer({ zoom: viewState.zoom });
    const [tool, tools, setTool] = useTools({
        hexMapModelContext,
        highlightLayerContext,
        viewStateContext,
    });
    const toolPaneProps = useMemo(() => ({ tool, tools, setTool }), [tool, tools]);
    const toolOptionsPaneProps = useMemo(() => ({
        tool,
        brushSizeSliderProps: { ...editorState, onChange: (brushSize) => updateEditorState({ brushSize }) },
        easelPaneProps: { ...editorState, setPigment: (pigment) => updateEditorState({ pigment }) }
    }), [tool, editorState]);
    const debugPaneProps = useMemo(() => ({
        visible: editorState.debug,
        keyValues: {
            pigment: colorOption.color,
            toolID: tool.id,
            brushSize: editorState.brushSize,
            clickCoordsOffset: maybe(PickingInfoPoint.toOffsetPoint)(editorState.priorClickEvent?.info.coordinate),
            clickCoordsPx: editorState?.priorClickEvent?.info?.coordinate,
            dragCoordsOffset: maybe(PickingInfoPoint.toOffsetPoint)(editorState.priorDragEvent?.info.coordinate),
            dragCoordsPx: editorState?.priorDragEvent?.info?.coordinate,
            gestureCoordsOffset: maybe(PickingInfoPoint.toOffsetPoint)(editorState.priorGestureEvent?.info.coordinate),
            gestureCoordsPx: editorState?.priorGestureEvent?.info?.coordinate,
            zoom: viewState.zoom,
        },
    }), [viewState, editorState]);
    // Toggle debug view
    useEffect(() => {
        const handler = (e) => e.key === '/' && e.ctrlKey && updateEditorState({ debug: !editorState.debug });
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [editorState.debug]);
    return (React.createElement("div", { className: cx(className, mapEditorWrapperStyle) },
        React.createElement("div", { className: editorPaneWrapperStyle },
            React.createElement("div", { className: css({ position: 'relative', flexGrow: 1, width: '100%' }) },
                React.createElement(DeckGLView, { hex2dLayerContext: hex2DLayerContext, highlightLayerContext: highlightLayerContext, tool: tool, viewStateContext: viewStateContext }),
                React.createElement(FloatingUI, { debugPaneProps, paletteProps, toolPaneProps, toolOptionsPaneProps })))));
};
export default HexMapEditor;
//# sourceMappingURL=index.js.map